import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITIES, STATUSES, fmtMoney } from "@/components/ticketMeta";
import { cn } from "@/lib/utils";

export const EMPTY_TICKET = {
  client_id: "", client_name: "", phone: "", address: "", equipment: "", description: "",
  priority: "medie", status: "nou", scheduled_date: "", technician_id: "", notes: "",
  cost_parts: "", cost_labor: "", source: "manual", raw_message: "",
};

export const toPayload = (v) => ({
  ...v,
  cost_parts: Number(v.cost_parts) || 0,
  cost_labor: Number(v.cost_labor) || 0,
});

const Field = ({ label, className, children }) => (
  <div className={cn("space-y-1.5", className)}>
    <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">{title}</h3>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

const PickSelect = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const TicketForm = ({ value, onChange, technicians = [], showStatus = true }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  const techOptions = [{ value: "none", label: "Nealocat" }, ...technicians.map((u) => ({ value: u.id, label: u.name }))];
  const total = (Number(value.cost_parts) || 0) + (Number(value.cost_labor) || 0);

  return (
    <div className="space-y-8" data-testid="ticket-form">
      <Section title="Client">
        <Field label="Nume client *">
          <Input data-testid="ticket-client-name-input" value={value.client_name} onChange={set("client_name")} placeholder="ex: Popescu Maria / SC Alfa SRL" />
        </Field>
        <Field label="Telefon">
          <Input data-testid="ticket-phone-input" value={value.phone} onChange={set("phone")} placeholder="07xx xxx xxx" inputMode="tel" />
        </Field>
        <Field label="Adresă" className="sm:col-span-2">
          <Input data-testid="ticket-address-input" value={value.address} onChange={set("address")} placeholder="Strada, număr, localitate" />
        </Field>
      </Section>

      <Section title="Problemă">
        <Field label="Echipament / model">
          <Input data-testid="ticket-equipment-input" value={value.equipment} onChange={set("equipment")} placeholder="ex: Centrală Viessmann Vitodens 050" />
        </Field>
        <Field label="Prioritate">
          <PickSelect testId="ticket-priority-select" value={value.priority} onChange={set("priority")} options={PRIORITIES} />
        </Field>
        <Field label="Descriere problemă *" className="sm:col-span-2">
          <Textarea data-testid="ticket-description-input" rows={4} value={value.description} onChange={set("description")} placeholder="Ce nu funcționează?" />
        </Field>
      </Section>

      <Section title="Intervenție">
        <Field label="Tehnician alocat">
          <PickSelect testId="ticket-technician-select" value={value.technician_id || "none"} onChange={(v) => onChange({ ...value, technician_id: v === "none" ? "" : v })} options={techOptions} />
        </Field>
        <Field label="Dată intervenție">
          <Input data-testid="ticket-date-input" type="date" value={value.scheduled_date} onChange={set("scheduled_date")} />
        </Field>
        {showStatus && (
          <Field label="Status">
            <PickSelect testId="ticket-status-select" value={value.status} onChange={set("status")} options={STATUSES} />
          </Field>
        )}
        <Field label="Note intervenție" className="sm:col-span-2">
          <Textarea data-testid="ticket-notes-input" rows={3} value={value.notes} onChange={set("notes")} placeholder="Ce s-a făcut, piese schimbate, observații..." />
        </Field>
        <Field label="Cost piese (lei)">
          <Input data-testid="ticket-cost-parts-input" type="number" min="0" step="0.01" value={value.cost_parts} onChange={set("cost_parts")} placeholder="0" />
        </Field>
        <Field label="Cost manoperă (lei)">
          <Input data-testid="ticket-cost-labor-input" type="number" min="0" step="0.01" value={value.cost_labor} onChange={set("cost_labor")} placeholder="0" />
        </Field>
        <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3 sm:col-span-2">
          <span className="text-sm text-muted-foreground">Total</span>
          <span className="font-mono text-lg font-semibold" data-testid="ticket-total-cost">{fmtMoney(total)}</span>
        </div>
      </Section>
    </div>
  );
};
